/**
 * Manual and broker-originated fills (U040, INC-0707): import through the shared command dialog,
 * provenance shown on every row, UNRECONCILED never hidden, and observed and simulated fills in
 * separate tables with separate totals: they are never added together (U039, V04-A12).
 */
import {
  FILLS_PATH,
  FILL_SIDES,
  FILL_SOURCE_KINDS,
  decodeFillListing,
  decodeFillRecord,
  type FillRecord,
  type TermDifference,
} from "../../contracts/account.ts";
import { ApiRequestError } from "../../app/client.ts";
import { paiseOnWire } from "../../app/command-dialog.ts";
import { openConfirmDialog, type DialogHandle } from "../../app/command-dialog-view.ts";
import { badge, definitions, instantCell, scrollRegion } from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import { formatInstant, formatPaise, formatQuantity } from "../../app/format.ts";
import { renderStateBanner, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle, ViewReader } from "../../app/view.ts";
import { handleOf, makeFrame } from "../frame.ts";

/** What the panel needs beyond projection reads: a plain GET and a POST with the CSRF header. */
export interface FillServices {
  get<T>(path: string, decode: (json: unknown) => T): Promise<T | null>;
  post<T>(path: string, body: unknown, decode: (json: unknown) => T): Promise<T | null>;
}

interface FullClient extends ViewReader {
  get<T>(path: string, params: URLSearchParams, decode: (json: unknown) => T): Promise<T | null>;
  post<T>(path: string, body: unknown, decode: (json: unknown) => T): Promise<T | null>;
}

function isFull(client: ViewReader): client is FullClient {
  return (
    "get" in client &&
    typeof client.get === "function" &&
    "post" in client &&
    typeof client.post === "function"
  );
}

/** Build the services from the view's API client (the full client, not a projection reader). */
export function fillServicesOf(ctx: ViewContext): FillServices {
  const client = ctx.client;
  const missing = (): Promise<never> =>
    Promise.reject(new Error("the fills panel needs the full API client"));
  return isFull(client)
    ? {
        get: (path, decode) => client.get(path, new URLSearchParams(), decode),
        post: (path, body, decode) => client.post(path, body, decode),
      }
    : { get: missing, post: missing };
}

export interface FillForm {
  readonly listing: string;
  readonly side: string;
  readonly quantity: string;
  readonly price: string;
  readonly fees: string;
  readonly sourceKind: string;
  readonly documentHash: string;
  readonly exchangeTime: string;
  readonly brokerReference: string;
}

/** The import body without its client key (the key is the intent's, added by the dialog). */
export interface FillImportBody {
  readonly account_id: string;
  readonly listing_id: string;
  readonly side: string;
  readonly quantity: number;
  readonly price_paise: number;
  readonly fees_paise: number;
  readonly source_kind: string;
  readonly document_hash: string;
  readonly exchange_time: string;
  readonly broker_reference: string | null;
}

const RUPEES = /^(\d{1,9})(?:\.(\d{1,2}))?$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;
const IDENT = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const SHA256 = /^[0-9a-f]{64}$/;

/** Exact rupees text (at most two decimals) as integer paise, or `null`; never a float. */
export function parseRupees(text: string): bigint | null {
  const match = RUPEES.exec(text.trim());
  if (match === null) {
    return null;
  }
  const [, whole = "0", fraction = ""] = match;
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
}

/** Why the form cannot be sent, or `null`. */
export function fillFormProblem(form: FillForm): string | null {
  const checks: readonly (readonly [boolean, string])[] = [
    [IDENT.test(form.listing.trim()), "Enter the listing, for example RELIANCE-EQ."],
    [(FILL_SIDES as readonly string[]).includes(form.side), "Choose buy or sell."],
    [/^[1-9]\d{0,8}$/.test(form.quantity.trim()), "Enter a whole quantity of at least 1."],
    [(parseRupees(form.price) ?? 0n) > 0n, "Enter the price in rupees, for example 1285.50."],
    [parseRupees(form.fees) !== null, "Enter the fees in rupees (0 when none)."],
    [
      (FILL_SOURCE_KINDS as readonly string[]).includes(form.sourceKind),
      "Choose where the fill comes from.",
    ],
    [
      SHA256.test(form.documentHash.trim()),
      "Enter the SHA-256 of the source document (64 lower-case hexadecimal characters).",
    ],
    [
      INSTANT.test(form.exchangeTime.trim()),
      "Enter the exchange time in UTC, for example 2026-10-09T03:50:12Z.",
    ],
    [
      form.brokerReference.trim() === "" || IDENT.test(form.brokerReference.trim()),
      "The broker reference is letters, digits and . _ : - only.",
    ],
  ];
  return checks.find(([ok]) => !ok)?.[1] ?? null;
}

/** The wire body of a valid form for `accountId` (call only when `fillFormProblem` is `null`). */
export function buildFillImport(form: FillForm, accountId: string): FillImportBody {
  const reference = form.brokerReference.trim();
  return {
    account_id: accountId,
    listing_id: form.listing.trim(),
    side: form.side,
    quantity: Number(form.quantity.trim()),
    price_paise: paiseOnWire(parseRupees(form.price)),
    fees_paise: paiseOnWire(parseRupees(form.fees)),
    source_kind: form.sourceKind,
    document_hash: form.documentHash.trim(),
    exchange_time: form.exchangeTime.trim(),
    broker_reference: reference === "" ? null : reference,
  };
}

/** The text of a refused import: what the server said, never a guess that it was recorded. */
export function importRefusal(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return "the server could not be reached; it may not have received the import";
  }
  const kind = error.body?.kind ?? null;
  const kinds = kind === null ? [] : typeof kind === "string" ? [kind] : [...kind];
  if (kinds.includes("DuplicateFillError")) {
    return "this fill is already recorded (same account, terms, document and exchange time)";
  }
  if (kinds.includes("IDEMPOTENCY_CONFLICT")) {
    return "this request identity was used for a different fill; start a new import";
  }
  if (error.status === 422) {
    return "the exchange time is after the server's time, so it cannot have happened yet";
  }
  return `the server refused it (status ${String(error.status)}${kinds.length === 0 ? "" : `, ${kinds.join(", ")}`})`;
}

const TERM_LABELS: Readonly<Record<TermDifference["term"], string>> = {
  QUANTITY: "Quantity",
  PRICE: "Price",
  FEES: "Fees",
};

/** One difference in words: recorded, broker and the signed difference, exactly. */
export function differenceText(difference: TermDifference): string {
  const sign = difference.difference > 0n ? "+" : "";
  if (difference.term === "QUANTITY") {
    return `Quantity: recorded ${formatQuantity(difference.recorded)}, broker ${formatQuantity(difference.broker)} (${sign}${difference.difference.toString()} shares)`;
  }
  return `${TERM_LABELS[difference.term]}: recorded ${formatPaise(difference.recorded)}, broker ${formatPaise(difference.broker)} (${sign}${difference.difference.toString()} paise)`;
}

const TONES = { UNRECONCILED: "warn", RECONCILED: "ok", MISMATCHED: "bad" } as const;

/** Totals of one evidence tag; the two tags are summed apart and never into one figure. */
export interface EvidenceTotals {
  readonly count: number;
  readonly unreconciled: number;
  readonly mismatched: number;
  readonly feesPaise: bigint;
}

/** Split fills by evidence tag and total each side apart (U039: never merged silently). */
export function totalsByEvidence(rows: readonly FillRecord[]): {
  readonly OBSERVED: EvidenceTotals;
  readonly SIMULATED: EvidenceTotals;
} {
  const of = (tag: FillRecord["evidence"]): EvidenceTotals => {
    const own = rows.filter((row) => row.evidence === tag);
    return {
      count: own.length,
      unreconciled: own.filter((row) => row.reconciliation === "UNRECONCILED").length,
      mismatched: own.filter((row) => row.reconciliation === "MISMATCHED").length,
      feesPaise: own.reduce((sum, row) => sum + row.feesPaise, 0n),
    };
  };
  return { OBSERVED: of("OBSERVED"), SIMULATED: of("SIMULATED") };
}

function provenance(row: FillRecord): HTMLElement {
  return h(
    "span",
    { class: "provenance", attrs: { "data-source": row.sourceKind } },
    `${row.sourceKind.replaceAll("_", " ").toLowerCase()}; document ${row.documentHash.slice(0, 12)}…; recorded by ${row.operator} at `,
    instantCell(row.recordedAt),
    "; executed ",
    instantCell(row.exchangeTime),
  );
}

function reconciliationCell(row: FillRecord): HTMLElement {
  return h(
    "span",
    {},
    badge(row.reconciliation, TONES[row.reconciliation]),
    row.statementId === null ? " no broker statement matched yet" : ` against ${row.statementId}`,
    ...row.differences.map((difference) =>
      h("span", { class: "flag" }, ` ${differenceText(difference)}`),
    ),
  );
}

/** Mount the fills panel (`#/portfolio/fills`). */
export function mountManualFills(
  ctx: ViewContext,
  services: FillServices = fillServicesOf(ctx),
): ViewHandle {
  const frame = makeFrame("fills", "Recorded fills");
  if (ctx.accountId === null) {
    frame.banner.append(renderStateBanner({ kind: "no-observations" }));
    return handleOf(frame, ctx);
  }
  const accountId = ctx.accountId;
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  let rows: FillRecord[] = [];
  let dialog: DialogHandle | null = null;
  ctx.scope.add(() => {
    dialog?.close();
  });
  const summary = h("p", { class: "note fills-summary", attrs: { role: "status" } });
  const lists = h("div", { class: "fills" });
  const inspector = h("section", {
    class: "fill-inspector",
    attrs: { "aria-live": "polite", "aria-label": "Fill details" },
  });

  const inspect = async (fillId: string): Promise<void> => {
    try {
      const record = await services.get(`${FILLS_PATH}/${fillId}`, (json) =>
        decodeFillRecord(json),
      );
      if (record === null || ctx.scope.disposed) {
        return;
      }
      setChildren(inspector, [
        h("h2", {}, `Fill ${record.fillId.slice(0, 8)}`),
        definitions([
          ["Evidence", badge(record.evidence, record.evidence === "OBSERVED" ? "ok" : "info")],
          ["Instrument", `${record.listingId} ${record.side}`],
          ["Quantity", formatQuantity(record.quantity)],
          ["Price", formatPaise(record.pricePaise)],
          ["Fees", formatPaise(record.feesPaise)],
          ["Source", record.sourceKind],
          ["Document SHA-256", h("code", {}, record.documentHash)],
          ["Recorded by", record.operator],
          ["Recorded at", formatInstant(record.recordedAt)],
          ["Exchange time", formatInstant(record.exchangeTime)],
          ["Broker reference", record.brokerReference ?? "none stated"],
          ["Reconciliation", reconciliationCell(record)],
          ["Version", `v${String(record.version)}`],
        ]),
      ]);
    } catch (error) {
      setChildren(inspector, [renderStateBanner(stateOfError(error))]);
    }
  };

  const table = (tag: FillRecord["evidence"], caption: string): HTMLElement | null => {
    const own = rows.filter((row) => row.evidence === tag);
    if (own.length === 0) {
      return null;
    }
    const headers = ["Fill", "Instrument", "Side", "Quantity", "Price", "Fees", "Evidence"];
    return scrollRegion(
      caption,
      h(
        "table",
        { class: "data-table", attrs: { "data-evidence": tag } },
        h("caption", {}, caption),
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            ...[...headers, "Provenance", "Reconciliation", "Inspect"].map((name) =>
              h("th", { attrs: { scope: "col" } }, name),
            ),
          ),
        ),
        h(
          "tbody",
          {},
          ...own.map((row) =>
            h(
              "tr",
              {
                attrs: {
                  "data-fill": row.fillId,
                  "data-evidence": row.evidence,
                  "data-reconciliation": row.reconciliation,
                },
              },
              ...(
                [
                  row.fillId.slice(0, 8),
                  row.listingId,
                  row.side,
                  formatQuantity(row.quantity),
                  formatPaise(row.pricePaise),
                  formatPaise(row.feesPaise),
                  badge(row.evidence, row.evidence === "OBSERVED" ? "ok" : "info"),
                  provenance(row),
                  reconciliationCell(row),
                  h(
                    "button",
                    {
                      attrs: {
                        type: "button",
                        "data-action": "inspect",
                        "aria-label": `Inspect fill ${row.fillId.slice(0, 8)} of ${row.listingId}`,
                      },
                      on: {
                        click: () => {
                          void inspect(row.fillId);
                        },
                      },
                    },
                    "Inspect",
                  ),
                ] satisfies Child[]
              ).map((cell) => h("td", {}, cell)),
            ),
          ),
        ),
      ),
    );
  };

  const draw = (): void => {
    const totals = totalsByEvidence(rows);
    summary.textContent = `Observed (actual executions): ${String(totals.OBSERVED.count)} fills, ${String(totals.OBSERVED.unreconciled)} unreconciled, ${String(totals.OBSERVED.mismatched)} mismatched, fees ${formatPaise(totals.OBSERVED.feesPaise)}. Simulated: ${String(totals.SIMULATED.count)} fills, fees ${formatPaise(totals.SIMULATED.feesPaise)}. The two are kept apart and never added together.`;
    setChildren(lists, [
      rows.length === 0 ? h("p", {}, "No fills recorded for this account.") : null,
      table("OBSERVED", "Observed fills (actual executions)"),
      table("SIMULATED", "Simulated fills (never added to observed)"),
    ]);
  };

  const upsert = (record: FillRecord): void => {
    rows = rows.some((row) => row.fillId === record.fillId)
      ? rows.map((row) => (row.fillId === record.fillId ? record : row))
      : [...rows, record];
    draw();
  };

  async function load(): Promise<void> {
    try {
      const answer = await services.get(
        `${FILLS_PATH}?account_id=${encodeURIComponent(accountId)}`,
        (json) => decodeFillListing(json),
      );
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      rows = [...answer];
      setChildren(frame.banner, []);
      draw();
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  const form = h("form", {
    class: "fill-form",
    attrs: { "aria-label": "Record or import a fill", novalidate: "" },
  });
  const problem = h("p", { class: "form-problem", attrs: { role: "alert" } });
  const submit = h("button", { attrs: { type: "submit" } }, "Review and import…");
  const input = (name: string, label: string, hint = ""): HTMLLabelElement =>
    h(
      "label",
      {},
      `${label} `,
      h("input", { attrs: { name, type: "text", autocomplete: "off", placeholder: hint } }),
    );
  const select = (name: string, label: string, values: readonly string[]): HTMLLabelElement =>
    h(
      "label",
      {},
      `${label} `,
      h(
        "select",
        { attrs: { name } },
        h("option", { attrs: { value: "" } }, "Choose…"),
        ...values.map((value) => h("option", { attrs: { value } }, value)),
      ),
    );
  form.append(
    input("listing", "Listing", "RELIANCE-EQ"),
    select("side", "Side", FILL_SIDES),
    input("quantity", "Quantity"),
    input("price", "Price (₹)", "1285.50"),
    input("fees", "Fees (₹)", "23.45"),
    select("source", "Source", FILL_SOURCE_KINDS),
    input("document", "Document SHA-256"),
    input("exchange", "Exchange time (UTC)", "2026-10-09T03:50:12Z"),
    input("reference", "Broker reference (optional)"),
    submit,
    problem,
  );
  const values = (): FillForm => {
    const get = (name: string): string =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value;
    return {
      listing: get("listing"),
      side: get("side"),
      quantity: get("quantity"),
      price: get("price"),
      fees: get("fees"),
      sourceKind: get("source"),
      documentHash: get("document"),
      exchangeTime: get("exchange"),
      brokerReference: get("reference"),
    };
  };
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const current = values();
    const issue = fillFormProblem(current);
    problem.textContent = issue ?? "";
    if (issue !== null) {
      return;
    }
    const body = buildFillImport(current, accountId);
    dialog?.close();
    dialog = openConfirmDialog(
      ctx.commands.keys,
      {
        // One identity per intent: the same body re-uses its key (retry, refresh, second tab).
        intentId: `fill-import|${JSON.stringify(body)}`,
        id: "import-fill",
        title: "Record this fill?",
        summary: [
          ["Account", accountId],
          ["Instrument", `${body.listing_id} ${body.side}`],
          ["Quantity", formatQuantity(body.quantity)],
          ["Price", formatPaise(body.price_paise)],
          ["Fees", formatPaise(body.fees_paise)],
          ["Source", body.source_kind],
          ["Document SHA-256", body.document_hash],
          ["Exchange time", body.exchange_time],
          ["Broker reference", body.broker_reference ?? "none stated"],
          ["Recorded as", "OBSERVED: an actual execution, never added to simulated results"],
          ["Reconciliation", "UNRECONCILED until a broker statement is matched to it"],
          ["Recorded by", "you, at the server's time; the import is audited"],
        ],
        confirmLabel: "Record the fill",
        run: async (clientKey) => {
          try {
            const record = await services.post(
              FILLS_PATH,
              { client_key: clientKey, ...body },
              (json) => decodeFillRecord(json),
            );
            if (record === null) {
              return "The view changed; the answer was not shown. Check the list before importing again.";
            }
            upsert(record);
            return `Recorded as ${record.fillId.slice(0, 8)}: ${record.evidence}, ${record.reconciliation}.`;
          } catch (error) {
            throw new Error(importRefusal(error), { cause: error });
          }
        },
      },
      submit,
    );
  });

  frame.body.append(
    h(
      "p",
      { class: "note" },
      "Record an execution you placed yourself or one the broker reports. Each fill keeps where it came from and stays UNRECONCILED until a broker statement agrees with it to the share and to the paisa.",
    ),
    form,
    summary,
    lists,
    inspector,
  );
  void load();
  return handleOf(frame, ctx);
}
